import { useParams, Navigate, Link } from 'react-router-dom'
import { getComparisonBySlug } from '@/data/comparisons'
import type { ComparisonFeature, KeyDifference, ContentSection } from '@/data/comparisons'
import { Button } from '@/components/ui'
import { SEO } from '@/components/seo'

// Comparison table row component
function ComparisonRow({
  feature,
}: {
  feature: ComparisonFeature
}) {
  const renderValue = (value: string) => {
    if (value === 'check') {
      return <span className="text-accent-teal text-xl">✓</span>
    }
    if (value === 'cross') {
      return <span className="text-white/30 text-xl">✗</span>
    }
    // Partial or custom text
    return <span className="text-star text-sm">{value}</span>
  }

  return (
    <div className="grid grid-cols-[2fr_1fr_1fr] max-md:grid-cols-[1.5fr_1fr_1fr] py-4 px-6 max-md:p-3 border-b border-white/5 last:border-b-0 items-center text-sm max-md:text-xs">
      <span className="text-brand-light/90">{feature.feature}</span>
      <span className="text-center">{renderValue(feature.wealthspace)}</span>
      <span className="text-center">{renderValue(feature.competitor)}</span>
    </div>
  )
}

// Key difference card
function KeyDifferenceCard({ item }: { item: KeyDifference }) {
  return (
    <div>
      <h4 className="text-base font-semibold text-brand-light mb-2">{item.title}</h4>
      <p className="text-sm text-brand-muted/70 leading-relaxed">{item.description}</p>
    </div>
  )
}

// Content section with list items
function ContentSectionBlock({ section }: { section: ContentSection }) {
  return (
    <section className="my-16">
      <h2 className="font-serif text-[28px] font-medium text-brand-light mb-6">
        {section.title} <span className="text-accent-teal">{section.titleAccent}</span>
      </h2>
      {section.intro && <p className="text-brand-muted/80 mb-4">{section.intro}</p>}
      <ul className="space-y-0">
        {section.items.map((item, index) => (
          <li
            key={index}
            className="py-3 pl-8 relative text-brand-light/85 border-b border-white/5 last:border-b-0"
          >
            <span className="absolute left-0 text-accent-teal font-bold">✓</span>
            {item}
          </li>
        ))}
      </ul>
    </section>
  )
}

export default function ComparePage() {
  const { competitor } = useParams<{ competitor: string }>()
  const comparison = competitor ? getComparisonBySlug(competitor) : undefined

  // If comparison doesn't exist, redirect to home
  if (!comparison) {
    return <Navigate to="/" replace />
  }

  return (
    <>
      <SEO
        title={comparison.seo.title}
        description={comparison.seo.description}
        keywords={comparison.seo.keywords}
        url={`/compare/${comparison.slug}`}
      />

      {/* Hero */}
      <section className="max-w-[900px] mx-auto pt-20 pb-[60px] px-6 text-center">
        <h1 className="font-serif text-[clamp(2rem,5vw,3rem)] font-medium leading-[1.2] mb-6">
          {comparison.hero.title}{' '}
          <span className="text-accent-teal">{comparison.hero.titleAccent}</span>
        </h1>
        <p className="text-lg text-brand-muted/80 max-w-[700px] mx-auto mb-8">
          {comparison.hero.subtitle}
        </p>
        <div className="flex gap-4 justify-center flex-wrap">
          <Button href="/book-demo" size="lg">
            Book a Demo
          </Button>
          <Button href="/features" variant="secondary" size="lg">
            See All Features
          </Button>
        </div>
      </section>

      {/* Content */}
      <main className="max-w-[900px] mx-auto px-6 pb-20">
        {/* Comparison Table */}
        <div className="bg-white/[0.03] border border-white/[0.08] rounded-2xl overflow-hidden my-12">
          {/* Header */}
          <div className="grid grid-cols-[2fr_1fr_1fr] max-md:grid-cols-[1.5fr_1fr_1fr] bg-white/[0.05] py-5 px-6 max-md:p-3 font-semibold border-b border-white/[0.08] text-sm max-md:text-xs">
            <span>Feature</span>
            <span className="text-accent-teal text-center">WealthSpace</span>
            <span className="text-center">{comparison.competitorName}</span>
          </div>
          {/* Rows */}
          {comparison.comparisonTable.map((feature, index) => (
            <ComparisonRow key={index} feature={feature} />
          ))}
        </div>

        {/* Key Differences */}
        <div className="bg-gradient-to-br from-accent-teal/10 to-accent-teal/5 border border-accent-teal/30 rounded-2xl p-8 my-12">
          <h3 className="font-serif text-[22px] text-accent-teal mb-5">
            {comparison.keyDifferences.title}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {comparison.keyDifferences.items.map((item, index) => (
              <KeyDifferenceCard key={index} item={item} />
            ))}
          </div>
        </div>

        {/* Why Choose WealthSpace */}
        <ContentSectionBlock section={comparison.whyChoose} />

        {/* When Competitor Might Be Right (optional) */}
        {comparison.whenCompetitor && (
          <ContentSectionBlock section={comparison.whenCompetitor} />
        )}

        {/* CTA Section */}
        <div className="bg-white/[0.03] border border-white/[0.08] rounded-2xl p-12 text-center my-16">
          <h2 className="font-serif text-[28px] font-medium text-brand-light mb-4">
            {comparison.cta.title}
          </h2>
          <p className="text-brand-muted/80 mb-6">{comparison.cta.subtitle}</p>
          <Button href="/book-demo" size="lg">
            {comparison.cta.buttonText}
          </Button>
        </div>

        {/* Back Link */}
        <div className="text-center mt-12">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-brand-subtle hover:text-brand-muted transition-colors"
          >
            <svg
              className="w-4 h-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
            Back to home
          </Link>
        </div>
      </main>
    </>
  )
}
